import React, { useState } from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTheme } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Link,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { getVendorProfile } from '../../services/vendorService';
import { getOrganizerProfile } from '../../services/eventService';
import { getUnreadNotificationCount } from '../../services/notificationService';
import { getUnreadMessageCount } from '../../services/messagingService';

const Navbar = () => {
  const theme = useTheme();
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const { currentUser, logout } = useAuth();
  const history = useHistory();
  const location = useLocation();

  const roleId = Number(currentUser?.role_id);

  // Fetch unread notification count for vendors and organizers
  const { data: unreadNotificationCount = 0 } = useQuery(
    'unreadNotificationCount',
    () => getUnreadNotificationCount(),
    {
      enabled: roleId === 1 || roleId === 2, // For vendors and organizers
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Fetch unread message count
  const { data: unreadMessageData } = useQuery(
    'unreadMessageCount',
    () => getUnreadMessageCount(),
    {
      enabled: !!currentUser && (roleId === 1 || roleId === 2), // For vendors and organizers
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Fetch vendor profile only for vendors
  const { data: vendorProfile, error: vendorProfileError } = useQuery(
    ['vendorProfile'],
    getVendorProfile,
    {
      enabled: !!currentUser && roleId === 1,
      retry: 1,
      onError: (error) => {
        console.error('Navbar - Failed to fetch vendor profile:', error);
      },
      onSuccess: (data) => {
        console.log('Navbar - Successfully fetched vendor profile:', data);
      }
    }
  );

  // Log vendor profile status
  console.log('Navbar - Vendor profile:', vendorProfile);
  console.log('Navbar - Vendor profile error:', vendorProfileError);



  // Fetch organizer profile if user is an organizer
  const { data: organizerProfile } = useQuery(
    ['organizerProfile'],
    getOrganizerProfile,
    {
      enabled: !!currentUser && roleId === 2,
      retry: false,
    }
  );

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMenuClick = (path) => {
    history.push(path);
    handleCloseNavMenu();
  };

  const handleLogout = async () => {
    try {
      await logout();
      history.push('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (currentUser) {
      history.push('/dashboard');
    } else {
      history.push('/');
    }
  };

  let pages = [];
  if (!currentUser) {
    pages = [
      { name: 'Find Events', path: '/events' },
    ];
  } else if (roleId === 2) {
    // Organizer
    pages = [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Create Event', path: '/create-event' },
        { name: 'My Events', path: '/my-events' },
      { name: 'Vendors', path: '/vendors' },
    ];
  } else if (roleId === 1) {
    // Vendor
    pages = [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Find Events', path: '/events' },
        { name: 'My Applications', path: '/applications' },
      { name: 'Notifications', path: '/notifications' },
      ];
  }

  const userMenuItems = [
    {
      name: 'Profile',
      path: currentUser?.role_id === 2 
        ? (organizerProfile ? `/organizers/${organizerProfile.organizer_id}` : '/create-organizer-profile')
        : (vendorProfile ? '/vendor-profile' : '/create-vendor-profile'),
    },
  ];

  return (
    <AppBar position="static" sx={{ 
      background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
      boxShadow: 'none',
    }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo - Desktop */}
          <EventIcon sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="#"
            onClick={handleLogoClick}
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontWeight: 700,
              color: 'white',
              textDecoration: 'none',
            }}
          >
            Vendoroll
          </Typography>

          {/* Mobile menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => (
                <MenuItem 
                  key={page.name} 
                  onClick={() => handleMenuClick(page.path)}
                  selected={location.pathname === page.path}
                >
                  <Typography textAlign="center">{page.name}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Logo - Mobile */}
          <EventIcon sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }} />
          <Typography
            variant="h5"
            noWrap
            component="a"
            href="#"
            onClick={handleLogoClick}
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontWeight: 700,
              color: 'white',
              textDecoration: 'none',
            }}
          >
            Vendoroll
          </Typography>

          {/* Desktop menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => (
              <Button
                key={page.name}
                component={RouterLink}
                to={page.path}
                onClick={handleCloseNavMenu}
                sx={{ 
                  my: 2, 
                  color: 'white', 
                  display: 'block',
                  backgroundColor: location.pathname === page.path ? 'rgba(255,255,255,0.1)' : 'transparent',
                }}
              >
                {page.name}
              </Button>
            ))}
          </Box>

          {/* Messages Icon */}
          {currentUser && (roleId === 1 || roleId === 2) && (
            <Box sx={{ flexGrow: 0, mr: 1 }}>
              <Tooltip title="Messages">
                <IconButton 
                  onClick={() => history.push('/messages')}
                  sx={{ color: 'white' }}
                >
                  <Badge badgeContent={unreadMessageData?.unread_count > 0 ? unreadMessageData.unread_count : null} color="error">
                    <MessageIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {/* Notifications Icon (Vendors and Organizers) */}
          {currentUser && (roleId === 1 || roleId === 2) && (
            <Box sx={{ flexGrow: 0, mr: 1 }}>
              <Tooltip title="Notifications">
                <IconButton 
                  onClick={() => history.push('/notifications')}
                  sx={{ color: 'white' }}
                >
                  <Badge badgeContent={unreadNotificationCount > 0 ? unreadNotificationCount : null} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* User menu */}
          {currentUser ? (
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="User menu">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar alt={currentUser.name}>
                    <PersonIcon />
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {userMenuItems.map((item) => (
                  <MenuItem
                    key={item.name}
                    onClick={() => {
                      history.push(item.path);
                      handleCloseUserMenu();
                    }}
                  >
                    <Typography textAlign="center">{item.name}</Typography>
                  </MenuItem>
                ))}
                <MenuItem onClick={handleLogout}>
                  <Typography textAlign="center">Logout</Typography>
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 0 }}>
              <Button
                component={RouterLink}
                to="/login"
                color="inherit"
                sx={{ mr: 1 }}
              >
                Login
              </Button>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                color="secondary"
                sx={{
                  background: 'rgb(225,181,204)',
                  color: 'rgb(85,39,100)',
                  '&:hover': {
                    background: 'rgb(211,172,203)',
                  },
                }}
              >
                Register
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;